import { Form, FormControl, InputGroup, Button, Tabs, Tab } from 'react-bootstrap';
import {
  TradeRule,
  TradeAction,
  Condition,
  SeriesOption,
  ConditionGroup,
} from '@/modules/trade-rule-builder/model/trade-rule-builder';
import type { NewsEvent } from '@/modules/calendar/export';
import { useState, useEffect, useRef } from 'react';
import type { SearchResultItem } from '@/core/search/export';
import { useClickOutside } from '@/shared/hooks/export';
import ConditionGroupEditor from './condition-group-editor';
import { searchApi } from '@/core/search/export';
import { selectApi } from '@/modules/calendar/export';
import TradeRuleValidation from './trade-rule-validation';
import { CountryFlag } from '@/shared/components/export';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

// Local extension for UI-only filters
type NewsEventWithFilters = NewsEvent & { filters?: { key: string; value: string }[] };

function getCountryFromEvent(event: NewsEvent): string {
  return (
    (event as NewsEventWithFilters)?.filters?.find(
      (f: { key: string; value: string }) => f.key === 'country',
    )?.value || ''
  );
}

function renderCountryFlagAndCodeFromCountry(country: string) {
  return country ? (
    <>
      <CountryFlag country={country} height={'12px'} />
      <span className="text-muted" style={{ marginLeft: 4 }}>
        [{country}]
      </span>
    </>
  ) : null;
}

function renderCountryFlagAndCode(event: NewsEvent) {
  return renderCountryFlagAndCodeFromCountry(getCountryFromEvent(event));
}

function formatEventDate(date: string | Date | undefined, options?: Intl.DateTimeFormatOptions) {
  if (!date) return '';
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  if (isNaN(dateObj.getTime())) return '';
  return dateObj.toLocaleString(
    undefined,
    options || {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    },
  );
}

function formatEventDisplay(item: string, country: string, date: string | Date | undefined) {
  const countryStr = country ? ` [${country}]` : '';
  const dateStr = formatEventDate(date);
  return `${item}${countryStr} (${dateStr})`;
}

interface Props {
  rule: TradeRule;
  onUpdateRuleField: <K extends keyof TradeRule>(
    ruleId: string,
    key: K,
    value: TradeRule[K],
  ) => void;
  onUpdateCondition: <K extends keyof Condition>(
    groupId: string,
    conditionId: string,
    key: K,
    value: Condition[K],
  ) => void;
  onUpdateGroupOperator: (groupId: string, value: 'AND' | 'OR') => void;
  onAddCondition: (groupId: string) => void;
  onAddGroup: (groupId: string) => void;
  onDeleteCondition?: (groupId: string, conditionId: string) => void;
  onDeleteGroup?: (groupId: string) => void;
  onAddAction: (ruleId: string) => void;
  onUpdateAction: <K extends keyof TradeAction>(
    ruleId: string,
    actionId: string,
    key: K,
    value: TradeAction[K],
  ) => void;
  onDeleteAction: (ruleId: string, actionId: string) => void;
  onDeleteRule: (ruleId: string) => void;
}

export default function TradeRuleCard({
  rule,
  onUpdateRuleField,
  onUpdateCondition,
  onUpdateGroupOperator,
  onAddCondition,
  onAddGroup,
  onDeleteCondition,
  onDeleteGroup,
  onAddAction,
  onUpdateAction,
  onDeleteAction,
  onDeleteRule,
}: Props) {
  const [open, setOpen] = useState(false);
  const [validationValues, setValidationValues] = useState<{
    [seriesId: string]: { value: string; valid: boolean };
  }>({});

  // Find all seriesIds in the rule (assuming conditions have seriesId)
  const getSeriesIds = (group: ConditionGroup | Condition): string[] => {
    let ids: string[] = [];
    function isConditionGroup(group: ConditionGroup | Condition): group is ConditionGroup {
      return Array.isArray((group as ConditionGroup).children);
    }
    if (isConditionGroup(group)) {
      for (const child of group.children) {
        if ('seriesId' in child && child.seriesId) ids.push(child.seriesId);
        if ('children' in child && Array.isArray(child.children))
          ids = ids.concat(getSeriesIds(child));
      }
    }
    return ids;
  };
  const seriesIds = Array.from(new Set(getSeriesIds(rule.rootGroup)));

  const initialEventSearch = rule.eventName
    ? formatEventDisplay(rule.eventName, rule.eventCountry || '', rule.timestamp)
    : '';

  const [eventSearch, setEventSearch] = useState(initialEventSearch);
  const [eventResults, setEventResults] = useState<SearchResultItem[]>([]);
  const [selectedEvent, setSelectedEvent] = useState<NewsEventWithFilters | null>(null);
  const [seriesOptions, setSeriesOptions] = useState<SeriesOption[]>([]);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [hasAutoSearched, setHasAutoSearched] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useClickOutside(dropdownRef, () => setShowDropdown(false));

  // Simple event details cache
  const eventDetailsCache: Record<string, NewsEvent> =
    (TradeRuleCard as unknown as { _eventDetailsCache?: Record<string, NewsEvent> })
      ._eventDetailsCache || {};
  (
    TradeRuleCard as unknown as { _eventDetailsCache?: Record<string, NewsEvent> }
  )._eventDetailsCache = eventDetailsCache;

  // Helper to add country filter for UI flag rendering, avoiding duplicates
  function addCountryFilter(event: NewsEvent, country?: string): NewsEventWithFilters {
    if (!country) return { ...event };
    const filters = Array.isArray((event as NewsEventWithFilters).filters)
      ? [...(event as NewsEventWithFilters).filters!]
      : [];
    if (!filters.some(f => f.key === 'country' && f.value === country)) {
      filters.push({ key: 'country', value: country });
    }
    return { ...event, filters };
  }

  useEffect(() => {
    if (open && !selectedEvent && rule.eventId) {
      (async () => {
        if (!rule.eventId) {
          setSeriesOptions([]);
          return;
        }
        const eventKey = rule.eventId.replace('event:', '');
        if (eventDetailsCache[eventKey]) {
          const details = eventDetailsCache[eventKey];
          const detailsWithFilters = addCountryFilter(details, rule.eventCountry);
          setSelectedEvent(detailsWithFilters);
          setSeriesOptions(details.dataSeriesEntries || []);
          return;
        }
        try {
          const details = await selectApi.getEvent(eventKey);
          eventDetailsCache[eventKey] = details;
          const detailsWithFilters = addCountryFilter(details, rule.eventCountry);
          setSelectedEvent(detailsWithFilters);
          setSeriesOptions(details.dataSeriesEntries || []);
        } catch (e) {
          setSeriesOptions([]);
        }
      })();
    }
    // eslint-disable-next-line
  }, [open, selectedEvent, rule.eventId, rule.eventCountry]);

  let eventHeader = '⚠️ No Event Selected';
  let eventCountry = null;
  if (selectedEvent) {
    const dateStr = formatEventDate(selectedEvent.date, { month: 'short', day: 'numeric' });
    eventCountry = renderCountryFlagAndCode(selectedEvent);
    eventHeader = `${dateStr ? ` (${dateStr})` : ''} ${selectedEvent.title || ''}`.trim();
  } else if (rule.eventName) {
    const dateStr = formatEventDate(rule.timestamp, { month: 'short', day: 'numeric' });
    eventHeader = `${dateStr ? `(${dateStr}) ` : ''}${rule.eventName}`;
    if (rule.eventCountry) {
      eventCountry = renderCountryFlagAndCodeFromCountry(rule.eventCountry);
    }
  }

  const [activeTab, setActiveTab] = useState('event');

  // Type guard for SearchResults
  function isSearchResult(obj: unknown): obj is SearchResultItem {
    return (
      typeof obj === 'object' &&
      obj !== null &&
      'uri' in obj &&
      typeof (obj as { uri?: unknown }).uri === 'string' &&
      'title' in obj &&
      typeof (obj as { title?: unknown }).title === 'string'
    );
  }

  const handleEventSearch = async (search: string) => {
    setEventSearch(search);
    setShowDropdown(true);
    setLoadingEvents(true);
    try {
      const data = await searchApi.search({
        pagination: { page: 1, pageSize: 100 },
        filters: { search, types: ['event'] },
      });
      // Group by dataReleaseId, keep earliest upcoming event for each
      const now = new Date();
      const earliestByRelease: { [releaseId: string]: SearchResultItem } = {};
      for (const ev of data.results || []) {
        if (!ev.date) continue;
        const evDate = new Date(ev.date);
        if (isNaN(evDate.getTime()) || evDate < now) continue;
        const releaseId = ev.filters?.find(f => f.key === 'dataReleaseId')?.value || 'unknown';
        if (
          !earliestByRelease[releaseId] ||
          !earliestByRelease[releaseId].date ||
          (earliestByRelease[releaseId].date &&
            evDate < new Date(earliestByRelease[releaseId].date!))
        ) {
          earliestByRelease[releaseId] = ev;
        }
      }
      setEventResults(Object.values(earliestByRelease));
    } catch (e) {
      setEventResults([]);
    }
    setLoadingEvents(false);
  };

  // Fetch event details and series when selected
  const handleSelectEvent = async (event: SearchResultItem) => {
    const country = event.filters?.find(f => f.key === 'country')?.value || '';
    const eventWithFilters = addCountryFilter(event as any, country);
    setSelectedEvent(eventWithFilters);

    // Format display value for search bar
    const item = event.title || 'Item';
    setEventSearch(formatEventDisplay(item, country, event.date));
    setShowDropdown(false);
    const eventKey = event.uri.replace('event:', '');
    if (eventDetailsCache[eventKey]) {
      const details = eventDetailsCache[eventKey];
      setSeriesOptions(details.dataSeriesEntries || []);
      onUpdateRuleField(rule.id, 'timestamp', details.date);
      onUpdateRuleField(rule.id, 'eventId', event.uri);
      onUpdateRuleField(rule.id, 'eventName', item);
      onUpdateRuleField(rule.id, 'eventCountry', country);
      return;
    }
    try {
      // Fetch event details
      const details = await selectApi.getEvent(eventKey);
      eventDetailsCache[eventKey] = details;
      setSeriesOptions(details.dataSeriesEntries || []);
      onUpdateRuleField(rule.id, 'timestamp', details.date);
      onUpdateRuleField(rule.id, 'eventId', event.uri);
      onUpdateRuleField(rule.id, 'eventName', item);
      onUpdateRuleField(rule.id, 'eventCountry', country);
    } catch (e) {
      setSeriesOptions([]);
      onUpdateRuleField(rule.id, 'eventId', event.uri);
      onUpdateRuleField(rule.id, 'eventName', item);
      onUpdateRuleField(rule.id, 'eventCountry', country);
    }
  };

  return (
    <div className="accordion-item mb-2" style={{ userSelect: 'text', WebkitUserSelect: 'text' }}>
      <div
        className="accordion-header d-flex align-items-center "
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
        onClick={e => {
          if ((e.target as HTMLElement).closest('.delete-rule-btn')) return;
          setOpen(v => !v);
        }}
      >
        <span
          style={{
            marginRight: 12,
            transition: 'transform 0.2s',
            display: 'inline-block',
            transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
          }}
        >
          ▶
        </span>
        {eventCountry && (
          <span style={{ marginRight: 4, display: 'flex', alignItems: 'center' }}>
            {eventCountry}
          </span>
        )}
        <span
          style={{
            fontWeight: 600,
            display: 'flex',
            fontSize: '1.1em',
            alignItems: 'center',
            flex: 1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            maxWidth: '100%',
          }}
        >
          {eventHeader}
        </span>
        <Button
          variant="danger"
          size="sm"
          className="delete-rule-btn ms-2"
          style={{
            padding: '2px 8px',
            fontSize: '1.1em',
            borderRadius: 6,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={e => {
            e.stopPropagation();
            onDeleteRule(rule.id);
          }}
          title="Delete Rule"
        >
          <FontAwesomeIcon icon={faTimes} />
        </Button>
      </div>
      {open && (
        <div className="accordion-body">
          <Tabs activeKey={activeTab} onSelect={k => k && setActiveTab(k)}>
            <Tab eventKey={'event'} title={'Event'} className={'text-light'}>
              <div className="d-flex align-items-start mt-3">
                <div className="flex-grow-1">
                  {/* Event Search */}
                  <Form.Group className="mb-2" style={{ position: 'relative' }}>
                    <FormControl
                      type="text"
                      size="sm"
                      placeholder="Search for event..."
                      value={eventSearch}
                      onChange={e => handleEventSearch(e.target.value)}
                      autoComplete="off"
                      onFocus={() => {
                        setShowDropdown(true);
                        if (eventSearch && !hasAutoSearched) {
                          handleEventSearch(eventSearch);
                          setHasAutoSearched(true);
                        }
                      }}
                    />
                    {loadingEvents && <div className="text-muted">Searching...</div>}
                    {showDropdown && eventSearch && eventResults.length > 0 && (
                      <div
                        ref={dropdownRef}
                        className="event-dropdown"
                        style={{
                          position: 'absolute',
                          zIndex: 10,
                          border: '1px solid #624d4dff',
                          width: '100%',
                          maxHeight: 200,
                          overflowY: 'auto',
                          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        }}
                      >
                        {eventResults.map(ev => {
                          if (!isSearchResult(ev)) return null;
                          const item = ev.title || 'Item';
                          const country = ev.filters?.find(f => f.key === 'country')?.value || '';
                          return (
                            <div
                              key={ev.uri}
                              style={{
                                padding: '8px',
                                cursor: 'pointer',
                                borderBottom: '1px solid #eee',
                                background: '#e6f7ff',
                              }}
                              onMouseDown={() => handleSelectEvent(ev)}
                            >
                              {formatEventDisplay(item, country, ev.date)}
                            </div>
                          );
                        })}
                      </div>
                    )}
                    {selectedEvent && (
                      <div className="mt-2">
                        <h4>Selected Event:</h4>
                        <div className={'h3 mt-2'}>
                          {/* Always use rule.eventCountry for the flag to ensure consistency */}
                          {renderCountryFlagAndCodeFromCountry(rule.eventCountry || '')}{' '}
                          {selectedEvent.title}{' '}
                          <span>
                            <div className={'font-monospace text-warning mt-2'}>
                              {formatEventDate(selectedEvent.date)}
                            </div>
                          </span>
                        </div>

                        <div>
                          <h4>Available Series:</h4>
                          <ul>
                            {seriesOptions.map(value => (
                              <li>{value.display}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    )}
                  </Form.Group>
                </div>
              </div>
            </Tab>
            <Tab eventKey={'condition'} title={'Condition'} disabled={!rule.eventId}>
              <div className={'mt-2'}>
                <ConditionGroupEditor
                  group={rule.rootGroup}
                  ruleId={rule.id}
                  onUpdateGroupOperator={onUpdateGroupOperator}
                  onUpdateCondition={onUpdateCondition}
                  onAddCondition={onAddCondition}
                  onAddGroup={onAddGroup}
                  onDeleteCondition={onDeleteCondition}
                  onDeleteGroup={onDeleteGroup}
                  seriesOptions={seriesOptions}
                />
              </div>
            </Tab>
            <Tab eventKey={'actions'} title={'Actions'} disabled={!rule.eventId}>
              <div className={'mt-2'}>
                Actions will be performed when the conditions of the rule are met.
              </div>
              <div className={'mb-2'}>
                <Button size="sm" onClick={() => onAddAction(rule.id)}>
                  + Action
                </Button>
              </div>

              <div>Configured actions:</div>
              {/* Actions */}
              {rule.actions.map((a, idx) => (
                <InputGroup size="sm" className="mb-2" key={a.id}>
                  <Form.Select
                    value={a.action}
                    onChange={e =>
                      onUpdateAction(rule.id, a.id, 'action', e.target.value as 'buy' | 'sell')
                    }
                    style={{ maxWidth: '25%', border: '1px solid #444' }}
                  >
                    <option value="buy">Buy</option>
                    <option value="sell">Sell</option>
                  </Form.Select>
                  <FormControl
                    type="number"
                    placeholder="Volume"
                    value={a.volume}
                    onChange={e =>
                      onUpdateAction(rule.id, a.id, 'volume', parseFloat(e.target.value))
                    }
                  />
                  <FormControl
                    placeholder="Symbol"
                    value={a.symbol}
                    onChange={e => onUpdateAction(rule.id, a.id, 'symbol', e.target.value)}
                  />
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => onDeleteAction(rule.id, a.id)}
                  >
                    &times;
                  </Button>
                </InputGroup>
              ))}
            </Tab>
            <Tab eventKey={'validation'} title={'Validation'} disabled={!rule.eventId}>
              <div className="p-2 mb-2 mt-2">
                <div className="mb-2 fw-bold">Validation</div>
                {seriesIds.length === 0 ? (
                  <div className="text-muted">No series found in this rule.</div>
                ) : (
                  <>
                    {seriesIds.map(seriesId => {
                      const found = seriesOptions?.find(
                        opt => String(opt.dataSeriesId) === String(seriesId),
                      );
                      const display = found ? found.display : seriesId;
                      return (
                        <InputGroup className="mb-2" key={seriesId} size="sm">
                          <InputGroup.Text
                            style={{
                              minWidth: 120,
                              flexGrow: 1,
                              border: '1px solid #5c5c5cff',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                            }}
                          >
                            {display}
                          </InputGroup.Text>
                          <FormControl
                            placeholder="Value"
                            value={validationValues[seriesId]?.value || ''}
                            style={{ maxWidth: 160 }}
                            onChange={e =>
                              setValidationValues((v: typeof validationValues) => ({
                                ...v,
                                [seriesId]: {
                                  ...v[seriesId],
                                  value: e.target.value,
                                },
                              }))
                            }
                          />
                        </InputGroup>
                      );
                    })}
                    {/* Render validation result and status */}
                    <div className="mt-3">
                      <TradeRuleValidation
                        rule={rule}
                        validationValues={validationValues}
                        seriesOptions={seriesOptions}
                      />
                    </div>
                  </>
                )}
              </div>
            </Tab>
          </Tabs>
        </div>
      )}
    </div>
  );
}
